import { useState, useEffect, useCallback } from 'react';
import { callBackbonePostApi } from '../services/backboneApiService';
import { BACKBONE_ACTIONS, EXERCISE_API_NAME_TO_ID } from '../config/exercises';

const createInitialState = () => {
  return Object.values(EXERCISE_API_NAME_TO_ID).reduce((acc, id) => {
    acc[id] = { details: null, chart: null };
    return acc;
  }, {});
};

const INITIAL_STATE = createInitialState();

const createEmptyExerciseStats = () => ({
  currentOrm: 0,
  maxOrm: 0,
  setCount: 0,
  repCount: 0,
  totalVolume: 0,
});

const formatTimestampToDateKey = (timestamp) => {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toSafeNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return Number.isFinite(num) && !Number.isNaN(num) ? num : defaultValue;
};

const transformSessionData = (sessions) => {
  const exerciseAggregates = {};
  const exerciseChartData = {};

  Object.values(EXERCISE_API_NAME_TO_ID).forEach((id) => {
    exerciseAggregates[id] = createEmptyExerciseStats();
    exerciseChartData[id] = {};
  });

  const sortedSessions = [...sessions].sort(
    (a, b) => a.startTimestamp - b.startTimestamp
  );

  sortedSessions.forEach((session) => {
    const dateKey = formatTimestampToDateKey(session.startTimestamp);

    session.exercises.forEach((exercise) => {
      const exerciseId = EXERCISE_API_NAME_TO_ID[exercise.exerciseName];
      if (!exerciseId) return;

      const agg = exerciseAggregates[exerciseId];
      const setCount = toSafeNumber(exercise.setCount);
      const repCount = toSafeNumber(exercise.repCount);
      const volume = toSafeNumber(exercise.volume);
      const finalOrm = toSafeNumber(exercise.finalOrm);

      agg.setCount += setCount;
      agg.repCount += repCount;
      agg.totalVolume += volume;

      if (finalOrm > 0) {
        agg.currentOrm = finalOrm;
        exerciseChartData[exerciseId][dateKey] = finalOrm;
      }

      if (finalOrm > agg.maxOrm) {
        agg.maxOrm = finalOrm;
      }
    });
  });

  const result = {};
  Object.entries(exerciseAggregates).forEach(([id, agg]) => {
    const hasData = agg.maxOrm > 0 || agg.setCount > 0;
    result[id] = {
      details: hasData
        ? {
            current_orm: agg.currentOrm,
            max_orm: agg.maxOrm,
            set_count: agg.setCount,
            rep_count: agg.repCount,
            total_volume: agg.totalVolume,
          }
        : null,
      chart:
        Object.keys(exerciseChartData[id]).length > 0
          ? exerciseChartData[id]
          : null,
    };
  });

  return result;
};

const useExercisePerformanceData = (userId) => {
  const [data, setData] = useState(INITIAL_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!userId) {
      setData(INITIAL_STATE);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await callBackbonePostApi({
        action: BACKBONE_ACTIONS.GET_USER_SESSION_EXERCISE_PERFORMANCE,
        userId,
      });

      const sessions = response?.data;
      if (!Array.isArray(sessions)) {
        setData(INITIAL_STATE);
        return;
      }

      const transformedData = transformSessionData(sessions);
      setData(transformedData);
    } catch (err) {
      setError(err);
      setData(INITIAL_STATE);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
};

export default useExercisePerformanceData;

